// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"
import {
  DetachTagCategoryRequestSchema,
  DetachTagCategoryResponse,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type DetachTagCategoryRpc = (request: Init<typeof DetachTagCategoryRequestSchema>) => Observable<DetachTagCategoryResponse>

export const detachTagCategory = (detachTagCategoryRpc: DetachTagCategoryRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.detachTagCategory.match),
      mergeMap(({ payload: detachTagCategoryRequest }) =>
        detachTagCategoryRpc(detachTagCategoryRequest)
          .pipe(
            mergeMap(_ => of(TimelineSlice.detachTagCategory({
              timeRowIds: detachTagCategoryRequest.target?.case === "timeRowIds" ? detachTagCategoryRequest.target.value.ids : undefined,
              timeGroupId: detachTagCategoryRequest.target?.case === "timeGroupId" ? detachTagCategoryRequest.target.value : "",
              tagCategoryId: detachTagCategoryRequest.tagCategoryId ?? "",
            }))),
            catchError(handleError),
          )
      ),
    )

const handleError = (e: unknown) => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.FailedPrecondition) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_detach_tag_category_failed_precondition",
            message: "Failed to detach tag category. Time row may be out of sync."
          }),
          messageType: "ERROR"
        })),
        of(Actions.onTimelineOutOfSync())
      )
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({
          id: "msg_detach_tag_category_failed_permission_denied",
          message: "Failed to detach tag category. You do not have permission to detach a tag category from the time row or timesheet." }),
        messageType: "ERROR",
      }))
    }
  }

  return of(showSnackBar({
    message: t({
      id: "msg_detach_tag_category_failed",
      message: "Failed to detach tag category. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const detachTagCategoryRpc = (request: Init<typeof DetachTagCategoryRequestSchema>): Observable<DetachTagCategoryResponse> =>
  executorMutation(() => timelineService.detachTagCategory(create(DetachTagCategoryRequestSchema, request)))

export default detachTagCategory(detachTagCategoryRpc)
